import { isFunction } from 'ramda-extension';
import React, { useRef } from 'react';
import { useHistory } from 'react-router';
import { useTranslation } from '../../contexts/translation';
import { Columns } from '../../pages/Reports/Assets/Columns';
import { SisDataGrid } from '../table';
import { IDataGrid } from './AssetsDataGrid';

interface Props extends IDataGrid {
  userCanCreate?: boolean;
  dataGridKey: string;
  heading: string;
  filterVisibility: boolean;
  setFilterVisibility: (e: boolean) => void;
  filterChanged?: (hasFilter: boolean) => void;
  noTopPadding?: boolean;
}

export const AssetsReportDataGrid = ({
  data,
  handleEdit,
  handleCopy,
  resetFilter,
  dataGridKey,
  dataGridRef,
  userCanCreate,
  heading,
  filterVisibility,
  setFilterVisibility,
  filterChanged,
  noTopPadding,
}: Props) => {
  const { translate } = useTranslation();
  const transFormName = 'Asset.List';
  const transFormReportBar = 'Reports.Bar';
  const history = useHistory();

  const hasFilterRef = useRef(false);
  const toolbarBaseItemsRef = useRef<any[] | null>(null);
  const badgeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const onToolbarPreparing = (e) => {
    if (!Array.isArray(e.toolbarOptions.items)) return;

    e.toolbarOptions.items.unshift({
      location: 'before',
      text: heading,
      visible: !filterVisibility,
    });

    e.toolbarOptions.items.unshift(
      {
        location: 'after',
        widget: 'dxButton',
        options: {
          visible: userCanCreate,
          icon: 'add',
          type: 'default',
          stylingMode: 'contained',
          onClick: () => history.push(`/assets/new/`),
        },
      },
      {
        location: 'after',
        widget: 'dxButton',
        options: {
          visible: true,
          text: translate!(filterVisibility ? 'Hide' : 'Show', transFormReportBar),
          type: 'normal',
          onClick: () => setFilterVisibility(!filterVisibility),
        },
      },
      {
        location: 'after',
        widget: 'dxButton',
        options: {
          icon: 'clear',
          type: 'info',
          text: translate!('Clear filter', transFormName),
          stylingMode: 'outlined',
          onClick: () => {
            if (resetFilter && isFunction(resetFilter)) {
              resetFilter();
            }
          },
        },
      },
    );

    toolbarBaseItemsRef.current = [...(e.toolbarOptions.items || [])];
  };

  const handleOptionChanged = (e: any) => {
    if (e.name === 'dataSource') return;
    const grid = e.component;
    if (badgeTimerRef.current !== null) clearTimeout(badgeTimerRef.current);
    badgeTimerRef.current = setTimeout(() => {
      badgeTimerRef.current = null;
      const combined = grid.getCombinedFilter();
      const hasFilterNow = !!combined;
      if (hasFilterRef.current !== hasFilterNow) {
        hasFilterRef.current = hasFilterNow;
        filterChanged && filterChanged(hasFilterNow);
      }
    }, 300);
  };

  return (
    <>
      <SisDataGrid
        enableXlsxExport
        exportName={'assets-data'}
        dataGridRef={dataGridRef}
        data={data}
        keyExpr={'id'}
        transFormName={transFormName}
        stateStoringKey={dataGridKey}
        onToolbarPreparing={onToolbarPreparing}
        columns={Columns(handleEdit, translate, handleCopy)}
        onCellDblClick={(e) => handleEdit && handleEdit(e)}
        pageSizeChangeVisible={true}
        enableGrouping
        onOptionChanged={handleOptionChanged}
      />
    </>
  );
};
